import React from "react";
import { Box, Grid, Typography } from "@material-ui/core";
import { Actions } from "../../screens/request/components/actions";
import { Form, Modal } from "@components";
import convertToHtml from "draftjs-to-html";
import { convertToRaw, EditorState } from "draft-js";
import * as Api from "@api";

type FeebackFormValues = {
  feedbackType: string;
  businessUnit: string;
  feedbackSubject: string;
  details: EditorState;
  screenshotURL: string;
};

type Key = keyof FeebackFormValues;
//
export const Feedback: React.FC = () => {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);
  const [pending, setPending] = React.useState(false);
  const [isOpenFeedbackSentModal, setIsOpenFeedbackSentModal] = React.useState(
    false
  );
  const form = Form.useManager<FeebackFormValues>({
    feedbackType: "",
    businessUnit: "",
    feedbackSubject: "",
    details: EditorState.createEmpty(),
    screenshotURL: "",
  });

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "feedbackType":
      case "businessUnit":
      case "feedbackSubject":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "details":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value.getCurrentContent().getPlainText()),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleSubmitButtonClick(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          setIsOpenConfirmationModal(true);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  function handleAddButtonClick(): void {
    setPending(true);
    Api.postFeedback({
      ...form.values,
      details: convertToHtml(
        convertToRaw(form.values.details.getCurrentContent())
      ),
    })
      .then(() => {
        setPending(false);
        setIsOpenConfirmationModal(false);
        setIsOpenFeedbackSentModal(true);
        form.reset();
      })
      .catch((error) => {
        setPending(false);
        // [TODO] Handle error
      });
  }

  return (
    <Box width="720px" mx="auto" mt={4} px={4} pb={2}>
      <Form>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center">
            <Box mr={1}>
              <i className="fal fa-comment-lines"></i>
            </Box>
            <Typography variant="body1">Send Feedback</Typography>
          </Box>
        </Grid>
        <Form.Dropdown
          label="Send Feedback"
          onChange={onChange}
          options={["System Issue", "Suggestion"]}
          name="feedbackType"
          size={6}
          value={form.values.feedbackType || ""}
          required
          error={form.errors.feedbackType}
        />
        <Form.Dropdown
          label="Business Unit"
          onChange={onChange}
          options={["General", "Global Payment Legacy", "Heartland"]}
          name="businessUnit"
          size={6}
          value={form.values.businessUnit || ""}
          required
          error={form.errors.businessUnit}
        />
        <Form.Textbox
          label="Subject"
          name="feedbackSubject"
          onChange={onChange}
          size={6}
          value={form.values.feedbackSubject}
          required
          error={form.errors.feedbackSubject}
        />
        <Form.RichTextEditor
          label="Details:"
          error={form.errors.details}
          required
          name="details"
          onChange={onChange}
          rows={5}
          rowsMax={5}
          size={12}
          value={form.values.details}
        />
        <Form.Textbox
          label="Screenshot URL"
          name="screenshotURL"
          onChange={onChange}
          size={6}
          value={form.values.screenshotURL}
          maxLength={2083}
        />
        <Grid item xs={12}>
          <Actions>
            <Actions.Action onClick={handleSubmitButtonClick}>
              Submit
            </Actions.Action>
          </Actions>
        </Grid>
      </Form>
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
            disabled: pending,
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to add this feedback?
        </Typography>
      </Modal>
      <Modal
        isOpen={isOpenFeedbackSentModal}
        positionButton="center"
        actions={[
          {
            label: "Ok",
            handler: () => {
              setIsOpenFeedbackSentModal(false);
            },
            variant: "outlined",
          },
        ]}
      >
        <Typography align="center" paragraph>
          Thank you for your feedback.
        </Typography>
      </Modal>
    </Box>
  );
};
